import { Box, CircularProgress, Typography } from '@mui/material';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { MessageSearchItem } from '../../models/message-search-dto';
import { MessageSearchResultItem } from './message-search-drawer.item';

interface MessageSearchResultsProps {
  searchTerm: string;
  items: MessageSearchItem[];
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetching: boolean;
  isLoading: boolean;
  error: Error | null;
  onSelect: (item: MessageSearchItem) => void;
}

export function MessageSearchResults({
  searchTerm,
  items,
  fetchNextPage,
  hasNextPage,
  isFetching,
  isLoading,
  error,
  onSelect
}: MessageSearchResultsProps) {
  const { t } = useTranslation();
  const parentRef = useRef<HTMLDivElement>(null);

  // Virtual list setup
  const virtualizer = useVirtualizer({
    count: items.length + (hasNextPage ? 1 : 0),
    estimateSize: () => 72,
    getScrollElement: () => parentRef.current,
    overscan: 8,
    gap: 2,
    getItemKey: (index) => items[index]?.id || `loading-${index}`
  });

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const el = parentRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    // Trigger load when 50px from bottom
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  // Throttled scroll listener
  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    let ticking = false;
    const throttledScroll = () => {
      if (ticking) return;
      requestAnimationFrame(() => {
        ticking = false;
        handleScroll();
      });
      ticking = true;
    };

    el.addEventListener('scroll', throttledScroll, { passive: true });
    return () => el.removeEventListener('scroll', throttledScroll);
  }, [handleScroll]);

  // Reset scroll on search term change
  useEffect(() => {
    virtualizer.scrollToOffset(0);
  }, [searchTerm, virtualizer]);

  // Error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Typography color='error' variant='body2'>
          {t('conversations.search.error', 'Search failed. Please try again.')}
        </Typography>
      </Box>
    );
  }

  // Initial loading state
  if (isLoading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Empty state
  if (!isLoading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
        <Typography color='text.secondary' variant='body2'>
          {t('conversations.search.empty', 'No messages found')}
        </Typography>
      </Box>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <Box
      sx={{ height: '100%' }}
      aria-label={t('conversations.search.results', 'Search results')}
      aria-live='polite'
      aria-atomic='true'
    >
      <Box ref={parentRef} sx={{ height: '100%', overflow: 'auto', scrollbarWidth: 'thin' }} aria-busy={isFetching}>
        <Box
          sx={{
            height: `${totalSize}px`,
            width: '100%',
            position: 'relative'
          }}
        >
          {virtualItems.map((virtualItem) => {
            // Loading indicator for infinite scroll
            if (virtualItem.index >= items.length) {
              return (
                <Box
                  key='loading-more'
                  ref={virtualizer.measureElement}
                  data-index={virtualItem.index}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    width: '100%',
                    height: 60,
                    transform: `translateY(${virtualItem.start}px)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  aria-label={t('conversations.search.loadingMore', 'Loading more results')}
                >
                  <CircularProgress size={20} />
                </Box>
              );
            }

            const item = items[virtualItem.index];

            return (
              <Box
                key={item.id}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                sx={{
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  pl: 2,
                  pr: 1,
                  transform: `translateY(${virtualItem.start}px)`
                }}
              >
                <MessageSearchResultItem item={item} searchTerm={searchTerm} onClick={onSelect} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
